/**
 * StudioLayout.js
 *
 * Root component for the AI Studio tab.
 * Renders: StudioSidebar (left) + StudioChat (right) + StudioResultModal (overlay).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  getProjects,
  createProject,
  hydrateProjectsFromDdb,
  getLastProjectId,
  setLastProjectId,
} from '../../services/studioProject.service';
import StudioSidebar from './StudioSidebar';
import StudioChat from './StudioChat';
import StudioResultModal from './StudioResultModal';
import './Studio.css';

const decodeRouteId = (raw) => {
  if (!raw) return null;
  try { return decodeURIComponent(raw); } catch { return raw; }
};

const studioPath = (projectId) =>
  projectId ? `/tools/studio/${encodeURIComponent(projectId)}` : '/tools/studio';

function StudioLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectIdRaw } = useParams();
  const routeProjectId = decodeRouteId(routeProjectIdRaw);

  const [projects,       setProjects]       = useState(getProjects);
  const [currentId,      setCurrentId]      = useState(() => {
    if (routeProjectId) return routeProjectId;
    const last = getLastProjectId();
    // Only restore if the project still exists
    return last && getProjects().some(p => p.id === last) ? last : null;
  });
  const [resultModal,    setResultModal]    = useState(null);
  const [finalizeAction, setFinalizeAction] = useState(null);
  const [modalPhaseLabel, setModalPhaseLabel] = useState('Requirements');

  const syncUrl = (projectId, replace = false) => {
    const target = studioPath(projectId);
    if (location.pathname !== target) {
      navigate(target, { replace });
    }
  };

  // Hydrate local cache from DynamoDB on first load.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const fresh = await hydrateProjectsFromDdb();
      if (!mounted) return;
      setProjects(fresh);

      const last = getLastProjectId();
      const routeExists = routeProjectId && fresh.some(p => p.id === routeProjectId);
      const next = routeExists
        ? routeProjectId
        : (last && fresh.some(p => p.id === last) ? last : (fresh[0]?.id || null));

      setCurrentId(next);
      setLastProjectId(next);
      syncUrl(next, true);
    })();

    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep selection in sync when user lands directly on /tools/studio/:projectId
  useEffect(() => {
    if (!projects.length) {
      setCurrentId(null);
      if (routeProjectId) {
        syncUrl(null, true);
      }
      return;
    }

    if (routeProjectId && projects.some((p) => p.id === routeProjectId)) {
      if (currentId !== routeProjectId) {
        setCurrentId(routeProjectId);
      }
      setLastProjectId(routeProjectId);
      return;
    }

    if (routeProjectId && !projects.some((p) => p.id === routeProjectId)) {
      const fallback = projects[0]?.id || null;
      setCurrentId(fallback);
      setLastProjectId(fallback);
      syncUrl(fallback, true);
      return;
    }

    // If user opens /tools/studio without id, canonicalize to active id.
    const fallback = (currentId && projects.some((p) => p.id === currentId))
      ? currentId
      : (getLastProjectId() && projects.some((p) => p.id === getLastProjectId())
        ? getLastProjectId()
        : (projects[0]?.id || null));

    if (fallback !== currentId) {
      setCurrentId(fallback);
    }
    setLastProjectId(fallback);
    syncUrl(fallback, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, routeProjectId]);

  // Keep project list in sync when underlying storage changes
  const refreshProjects = (deletedId) => {
    const fresh = getProjects();
    setProjects(fresh);
    // If the deleted project was active, deselect it
    if (deletedId && deletedId === currentId) {
      const next = fresh[0]?.id || null;
      setCurrentId(next);
      setLastProjectId(next);
      syncUrl(next, true);
      return;
    }

    // If current selection no longer exists, move to first available.
    if (currentId && !fresh.some((p) => p.id === currentId)) {
      const next = fresh[0]?.id || null;
      setCurrentId(next);
      setLastProjectId(next);
      syncUrl(next, true);
    }
  };

  const handleNewProject = () => {
    const p = createProject('New Project');
    refreshProjects();
    setCurrentId(p.id);
    setLastProjectId(p.id);
    syncUrl(p.id);
  };

  const handleSelectProject = (id) => {
    setCurrentId(id);
    setLastProjectId(id);
    syncUrl(id);
  };

  const handleProjectUpdated = () => {
    refreshProjects();
  };

  return (
    <div className="studio-layout">
      {/* Sidebar */}
      <StudioSidebar
        projects={projects}
        currentId={currentId}
        onNew={handleNewProject}
        onSelect={handleSelectProject}
        onUpdated={refreshProjects}
      />

      {/* Main area */}
      {currentId ? (
        <StudioChat
          key={currentId}           /* remount when project changes */
          projectId={currentId}
          onProjectUpdated={handleProjectUpdated}
          onShowResult={(result, options = {}) => {
            setResultModal(result);
            setFinalizeAction(() => options.onFinalizePhase || null);
            setModalPhaseLabel(options.phaseLabel || 'Requirements');
          }}
        />
      ) : (
        <EmptyState onNew={handleNewProject} />
      )}

      {/* Analysis result modal */}
      {resultModal && (
        <StudioResultModal
          result={resultModal}
          onFinalizePhase={finalizeAction}
          phaseLabel={modalPhaseLabel}
          onClose={() => {
            setResultModal(null);
            setFinalizeAction(null);
            setModalPhaseLabel('Requirements');
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="studio-empty">
      <div className="studio-empty-icon">🤖</div>
      <h2>AI Studio</h2>
      <p>
        Start a new conversation and let the AI guide you through gathering all
        your project requirements — system type, users, scale, goals,
        capabilities, NFRs, DoD, and security/compliance.
      </p>
      <button className="studio-empty-btn" onClick={onNew}>
        ✦ New Chat
      </button>
    </div>
  );
}

export default StudioLayout;
